import { action, computed, observable } from 'mobx';
import api from 'core/util/api';
import GlobalFilterCollection from 'app/stores/globalFilter/GlobalFilterCollection';

export class GlobalFilterStore {
  collection = new GlobalFilterCollection();

  /** Scopes the current user may select: every scope in their company. */
  @observable.ref
  available = [];

  /** Ephemeral selection: the server holds it on the session, never on the user record. */
  @observable
  activeScopeId = null;

  @observable.ref
  activeScope = null;

  @observable
  activeScopeLoading = false;

  /** Incremented on every applied change so handlers can discard responses from a prior scope. */
  @observable
  scopeGeneration = 0;

  scopeChangeHandlers = new Set();

  constructor(options = {}) {
    Object.assign(this, options);
  }

  // NOTE: the admin `collection` is only readable with the `rbux.scope::read` permission and is
  // still fetched lazily by the pages that need it. The active scope is not permission gated, so
  // it is loaded on boot to seed the navbar selector.
  initialize() {
    return this.loadActiveScope();
  }

  @computed
  get selectOptions() {
    return this.collection.unfiltered.map((model) => ({
      value: model.id,
      label: model.get('name') || 'Unnamed'
    }));
  }

  @computed
  get activeScopeOptions() {
    return this.available.map((scope) => ({
      value: scope.id,
      label: scope.name || 'Unnamed'
    }));
  }

  /**
   * Consumers register here to refetch when the scope changes. The scope is held on the server
   * session, so it is absent from request URLs and nothing invalidates itself; every affected
   * store has to be told explicitly.
   */
  registerScopeChangeHandler = (handler) => {
    this.scopeChangeHandlers.add(handler);
    return () => this.scopeChangeHandlers.delete(handler);
  };

  /**
   * Collections that were never loaded are skipped so unvisited pages stay lazy, and data is
   * replaced rather than cleared first so a failed refetch does not empty the view.
   */
  registerCollections = (collections) =>
    this.registerScopeChangeHandler(() =>
      Promise.all(
        collections.filter((collection) => collection.hasFetched).map((collection) => collection.fetch({ force: true }))
      )
    );

  /**
   * Bumps the generation before fanning out so late responses from the previous scope can be
   * identified and discarded by their handlers.
   */
  @action
  notifyScopeChange = () => {
    this.scopeGeneration += 1;
    const generation = this.scopeGeneration;
    const payload = { scope: this.activeScope, scopeId: this.activeScopeId, generation };

    return Promise.all(
      [...this.scopeChangeHandlers].map((handler) => Promise.resolve().then(() => handler(payload)))
    ).then(() => payload);
  };

  @action
  setActiveScopeState = (response) => {
    this.activeScopeId = response?.scope_id ?? null;
    this.activeScope = response?.scope ?? null;
    this.available = response?.available ?? [];
  };

  /** Loads the session's current selection; safe to call for any authenticated user. */
  loadActiveScope = () => {
    if (this.store.$app.isSubtenant) {
      return Promise.resolve();
    }

    this.activeScopeLoading = true;
    return api
      .get('/api/ui/rbux/scopes/active')
      .then(this.setActiveScopeState)
      .finally(() => {
        this.activeScopeLoading = false;
      });
  };

  /**
   * Persists the selection to the session, then fans out to registered handlers. The server
   * re-validates entitlement and returns the resolved scope, so locally held scope contents are
   * never trusted.
   */
  selectActiveScope = (scopeId) => {
    if (this.store.$app.isSubtenant) {
      return Promise.resolve();
    }

    this.activeScopeLoading = true;
    return api
      .put('/api/ui/rbux/scopes/active', { data: { scope_id: scopeId ?? null } })
      .then(this.setActiveScopeState)
      .then(this.notifyScopeChange)
      .finally(() => {
        this.activeScopeLoading = false;
      });
  };

  /**
   * Admin pages edit scopes out of band of the session's selection, so the navbar snapshot has
   * to be refetched. Editing the active scope also changes what it resolves to, which consumers
   * can only learn about by being told.
   */
  onScopeMutated = (scopeId) => {
    const affectsActiveScope = this.activeScopeId != null && Number(scopeId) === Number(this.activeScopeId);

    return this.loadActiveScope().then(() => (affectsActiveScope ? this.notifyScopeChange() : undefined));
  };
}

export default new GlobalFilterStore();
